import { useCallback, useEffect, useState } from "react";
import { MIRROR_REFRESHED_EVENT } from "./sync.js";

export interface Async<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export interface UseAsyncOptions {
  /**
   * Opt in only for reads whose source of truth is the booking mirror.
   *
   * A manual diary refresh used to reload every `useAsync` consumer in the
   * mounted app, including unrelated configuration and Action Centre queues.
   * That turned one refresh into a request storm. Mirror-backed reads declare
   * the dependency explicitly instead.
   */
  reloadOnMirrorRefresh?: boolean;
}

/**
 * Load something from the API, with the three states a screen actually has to
 * draw: loading, failed, and loaded.
 *
 * `deps` behaves like the dependency array it is passed to. A result that
 * arrives after the inputs changed is discarded rather than rendered, so
 * clicking quickly through students cannot leave the wrong one on screen.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  options: UseAsyncOptions = {},
): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    // Callers must see a requested read as busy in the very next render,
    // before the effect starts it, so another effect cannot enqueue it again.
    setLoading(true);
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!options.reloadOnMirrorRefresh) return undefined;
    const onMirrorRefreshed = () => reload();
    window.addEventListener(MIRROR_REFRESHED_EVENT, onMirrorRefreshed);
    return () => window.removeEventListener(MIRROR_REFRESHED_EVENT, onMirrorRefreshed);
  }, [options.reloadOnMirrorRefresh, reload]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);

    load()
      .then((value) => {
        if (current) setData(value);
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : "Something went wrong.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
